/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import { useState } from "react";
import { FiEye, FiEyeOff } from "react-icons/fi";

export default function ResetPasswordUI({
  isArabic,
  email,
  setEmail,
  phone,
  setPhone,
  newPassword,
  setNewPassword,
  showPassword,
  setShowPassword,
  passwordStrength,
  getStrengthColor,
  getStrengthValue,
  loading,
  isMobile,
  COLORS,
  handleSubmit,
}) {
  const FIELD_BG = "#6E3B8E";
  const FIELD_BORDER = "#9B6BC2";
  const [phoneLocked, setPhoneLocked] = useState(true);
  const [passwordLocked, setPasswordLocked] = useState(true);

  const fieldStyle = {
    background: FIELD_BG,
    color: "#fff",
    borderRadius: 10,
    border: `1px solid ${FIELD_BORDER}`,
    boxShadow: "inset 0 2px 4px rgba(0,0,0,0.2)",
    fontSize: isMobile ? "0.9rem" : "1rem",
    padding: isMobile ? "8px 12px" : "10px 15px",
  };


  return (
    <div
      style={{
        background: COLORS.gradient,
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "40px 15px",
        fontFamily: "'Tajawal', 'Cairo', sans-serif",
        color: COLORS.textLight,
      }}
    >
      <div
        style={{
          maxWidth: 450,
          width: "100%",
          background: "rgba(255,255,255,0.08)",
          border: "1px solid rgba(255,255,255,0.15)",
          borderRadius: 18,
          padding: isMobile ? "30px 20px" : "40px 30px",
          boxShadow: "0 8px 25px rgba(0,0,0,0.3)",
          backdropFilter: "blur(10px)",
          direction: isArabic ? "rtl" : "ltr",
        }}
      >
        {/* العنوان */}
        <h3 className="fw-bold text-center mb-2" style={{ color: COLORS.gold }}>
          {isArabic ? "إعادة تعيين كلمة المرور" : "Reset Password"}
        </h3>

        {/* الوصف */}
        <p
          className="text-center mb-4"
          style={{ fontSize: "0.95rem", opacity: 0.85 }}
        >
          {isArabic
            ? "أدخل بريدك الإلكتروني وكلمة المرور الجديدة للوصول إلى حسابك."
            : "Enter your email and new password to access your account."}
        </p>

        <form onSubmit={handleSubmit} autoComplete="off">
          {/* حقول وهمية لمنع تعبئة المتصفح التلقائية */}
          <input
            type="text"
            name="prevent_autofill_user"
            autoComplete="username"
            tabIndex={-1}
            aria-hidden="true"
            style={{ position: "absolute", opacity: 0, height: 0, width: 0, pointerEvents: "none" }}
          />
          <input
            type="password"
            name="prevent_autofill_pass"
            autoComplete="current-password"
            tabIndex={-1}
            aria-hidden="true"
            style={{ position: "absolute", opacity: 0, height: 0, width: 0, pointerEvents: "none" }}
          />

          {/* الإيميل */}
          <label className="text-white mb-1">
            {isArabic ? "البريد الإلكتروني" : "Email"}
          </label>
          <input
            className="form-control mb-3"
            type="email"
            name="email_reset"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="off"
            style={fieldStyle}
          />

          {/* الهاتف */}
          <label className="text-white mb-1">
            {isArabic ? "رقم الهاتف" : "Phone"}
          </label>
          <input
            className="form-control mb-3"
            type="text"
            name="phone_reset"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            readOnly={phoneLocked}
            onFocus={() => setPhoneLocked(false)}
            style={fieldStyle}
          />

          {/* كلمة المرور */}
          <label className="text-white mb-1">
            {isArabic ? "كلمة المرور الجديدة" : "New Password"}
          </label>
          <div className="position-relative mb-2">
            <input
              type={showPassword ? "text" : "password"}
              className="form-control"
              name="new_password_reset"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              autoCorrect="off"
              spellCheck={false}
              readOnly={passwordLocked}
              onFocus={() => setPasswordLocked(false)}
              style={{
                ...fieldStyle,
                paddingInlineEnd: isMobile ? "42px" : "48px",
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? (isArabic ? "إخفاء كلمة المرور" : "Hide password") : (isArabic ? "إظهار كلمة المرور" : "Show password")}
              title={showPassword ? (isArabic ? "إخفاء كلمة المرور" : "Hide password") : (isArabic ? "إظهار كلمة المرور" : "Show password")}
              style={{
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                [isArabic ? "left" : "right"]: "12px",
                background: "transparent",
                border: "none",
                color: "#fff",
                padding: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                opacity: 0.9,
              }}
            >
              {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
            </button>
          </div>

          {/* شروط كلمة المرور */}
          {newPassword && (
            <>
              <ul
                style={{
                  fontSize: "0.8rem",
                  marginBottom: "8px",
                  lineHeight: 1.8,
                  color: "#fff",
                }}
              >
                <li>
                  {isArabic
                    ? "٨ أحرف على الأقل"
                    : "At least 8 characters"}
                </li>
                <li>
                  {isArabic
                    ? "تحتوي على حرف كبير وصغير"
                    : "Uppercase and lowercase letters"}
                </li>
                <li>
                  {isArabic ? "تحتوي على رقم" : "Contains a number"}
                </li>
                <li>
                  {isArabic
                    ? "تحتوي على رمز خاص (!@#$)"
                    : "Contains a special character (!@#$)"}
                </li>
              </ul>

              {/* شريط القوة */}
              <div
                style={{
                  width: "100%",
                  height: "6px",
                  borderRadius: "6px",
                  background: "rgba(255,255,255,0.2)",
                  marginBottom: "8px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: getStrengthValue(),
                    height: "100%",
                    background: getStrengthColor(),
                    transition: "width 0.3s ease",
                  }}
                />
              </div>

              {/* رسالة القوة */}
              <p
                style={{
                  marginBottom: "15px",
                  fontSize: "0.85rem",
                  fontWeight: "600",
                  color: getStrengthColor(),
                }}
              >
                {isArabic
                  ? getStrengthValue() === "100%"
                    ? "كلمة المرور قوية ✅"
                    : getStrengthValue() === "60%"
                      ? "كلمة المرور متوسطة ⚠️"
                      : "كلمة المرور ضعيفة ❌"
                  : getStrengthValue() === "100%"
                    ? "Strong Password ✅"
                    : getStrengthValue() === "60%"
                      ? "Medium Password ⚠️"
                      : "Weak Password ❌"}
              </p>
            </>
          )}

          {/* زر الإرسال */}
          <button
            className="btn w-100 fw-semibold py-2 mt-3"
            disabled={loading}
            style={{
              background: COLORS.gold,
              color: COLORS.darkPurple,
              borderRadius: 10,
              boxShadow: "0 4px 15px rgba(242,180,65,0.4)",
            }}
          >
            {loading
              ? isArabic
                ? "جارٍ الإرسال..."
                : "Sending..."
              : isArabic
                ? "تغيير كلمة المرور"
                : "Change Password"}
          </button>
        </form>
      </div>
    </div>
  );
}
